import { Params } from '@angular/router';
import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { flatten, isEmpty } from 'lodash-es';

import {
  AccessList,
  AccessListCommercialData,
  AccessListUserByCustomerNumber,
  AccessListUserData,
  Account,
  Address,
  AggregatedStatus,
  BuildingInfo,
  CascadeMembersProperties,
  CascadeSequenceProperties,
  CascadeSquenceValuesCommands,
  CascadeSquenceValuesProperties,
  CompanyUser,
  Device,
  DeviceRoles,
  DynamicAssistantPlan,
  EmsPowerBalanceProperties,
  EquipmentDocumentResponse,
  Feature,
  FeatureNames,
  FeaturePowerLimitationOverview,
  Gateway,
  GatewayFirmwareCommands,
  GatewayFirmwareProperties,
  GenericNumberValueProperty,
  InvitationAccessLevel,
  IsActiveProperty,
  MaintainerAccessAccount,
  MonetizationFeature,
  OwnershipType,
  PaidFeature,
  PowerLimitationMethod,
  PowerLimitationOverviewProperties,
  PowerLimitationType,
  RemoteDiagnosticsCommands,
  RemoteDiagnosticsProperties,
  SubscriptionPlan,
  communicationModuleProperties,
  trendingProperties,
  User,
  VentilationFilterInformationProperties,
  VentilationLockExternalProperties,
  VentilationSensorsActuatorSelftestProperties,
  ViCarePlan,
} from '@vi-iot/client';

import {
  BookingSelectors,
  ConfigurationSelectors,
  DevicesState,
  DeviceTypes,
  GatewayState,
  getDevicePlatformBasingOnRoles,
  hasMainDeviceRole,
  HemsSelectors,
  InstallationFeaturesSelectors,
  InstallationType,
  isReady,
  isSolarLogInverter,
  isWallboxDevice,
  isWallboxGateway,
  LoadingStatus,
  parseVersion,
  RemoteConfigSelectors,
  RemoteConfigState,
  RemoteDiagnosticsSelectors,
  RemoteServiceSelectors,
  RichDevice,
  selectQueryParams,
} from '@store';

import {
  isPaidFeatureAvailable,
  PaidFeaturesEnum,
} from '../../../app/shared/pipes/paid-feature-available/paid-feature-available.pipe';
import { DevicesSelectors } from '../../@store/devices/devices.selectors'; // need to be like that because of cyclical dependencies issues
import { Indicator } from '../_services';
import { AppState } from '../app.state';
import { AuthSelectors, AuthState, MeUserDetails } from '../auth';
import { InstallationPackageStatus, InstallationsSelectors } from '../installations';

import { AzureCertificateValidVersions } from '../../shared/common/azure-cert-valid-versions';
import {
  AddressDetails,
  CascadeLagDeviceDetails,
  DashboardTypes,
  DeviceDetails,
  GatewayStatus,
  GatewayTypeLegacy,
  GenericStoreStatus,
  InstallationDevicePanels,
  InstallationPanelOrder,
  OwnerDetails,
  partnerShopLinkVkOrgs,
  Platform,
} from './installation-details.model';
import { HemsAdvancedAnalysisStatus, InstallationDetailsState } from './installation-details.state';
import { InstallationDetailsMappers } from './installation-details.mappers';
import { InstallationFeaturesState } from '../installation-features/installation-features.state';
import { InstallationFeaturesHelpers } from '../installation-features/installation-features.helpers';

export namespace InstallationDetailsSelectors {
  export const installationDetails = (state: AppState): InstallationDetailsState =>
    state.installationDetails;
  export const devsState = (state: AppState): DevicesState => state.devices;
  export const gtwState = (state: AppState): GatewayState => state.gateway;
  export const remoteConfig = (state: AppState): RemoteConfigState => state.remoteConfig;

  export const authState = (state: AppState): AuthState => state.auth;

  export function isMenuBarVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      getLoadedIndicator,
      DevicesSelectors.getIndicators(),
      InstallationFeaturesSelectors.selectFeaturesLoading,
      getGatewayStatus(),
      DevicesSelectors.getSelectedDevice,
      (isLoaded, deviceIndicators, featuresLoading, gatewayStatus, selectedDevice) => {
        if (selectedDevice) {
          return (
            !deviceIndicators.loading &&
            !featuresLoading &&
            !gatewayStatus.remoteDiagnostic &&
            !isWallboxDevice(selectedDevice) &&
            !isSolarLogInverter(selectedDevice)
          );
        }

        return (
          isLoaded &&
          !deviceIndicators.loading &&
          !featuresLoading &&
          !gatewayStatus.remoteDiagnostic
        );
      }
    );
  }

  export function getState(): MemoizedSelector<
    AppState,
    InstallationDetailsState,
    DefaultProjectorFn<InstallationDetailsState>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) => state);
  }

  export function getLoadingIndicator(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) =>
        state.loading ||
        state.vicarePlans.loading ||
        state.monetizationFeatures.loading ||
        state.serviceInstruction.loading ||
        state.updateBuildingInfoStatus.loading ||
        state.installation.loading
    );
  }

  export function getAvailablePaidFeatures(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      installationDetails,
      AuthSelectors.getAvailablePaidCompanyFeatures(),
      (state: InstallationDetailsState, availableCompanyPaidFeatures: string[]) => [
        ...new Set([...state.availablePaidFeatures, ...availableCompanyPaidFeatures]),
      ]
    );
  }

  export function getLoadedIndicator(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) => state.loaded);
  }

  export function getPanelsOrderLoadedIndicator(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state.panelsOrder.loaded
    );
  }

  export function getSelectedGateway(): MemoizedSelector<
    AppState,
    Gateway,
    DefaultProjectorFn<Gateway>
  > {
    return createSelector(
      installationDetails,
      DevicesSelectors.getSelectedDevice,
      selectQueryParams,
      (state: InstallationDetailsState, selectedDevice: RichDevice, queryParams: Params) => {
        const gatewaySerial: string = selectedDevice?.gatewaySerial ?? queryParams?.gatewaySerial;

        return state?.installation?.value?.gateways?.find(
          (gateway) => gateway?.serial === gatewaySerial
        );
      }
    );
  }

  export const getInstallationGateways = createSelector(
    installationDetails,
    (state: InstallationDetailsState): Gateway[] => {
      return state?.installation?.value?.gateways;
    }
  );

  export const isMultigatewayInstallation = createSelector(
    installationDetails,
    (state: InstallationDetailsState): boolean => {
      return state?.installation?.value?.gateways?.length > 1;
    }
  );

  export function isSelectedGatewayACascade(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.getDevicesOfSelectedGateway(),
      (devicesWithFeatures: RichDevice[]) =>
        devicesWithFeatures?.some(
          (device) =>
            device.roles.includes(DeviceRoles.CascadeLead) ||
            device.roles.includes(DeviceRoles.CascadeV2Lead) ||
            device.roles.includes(DeviceRoles.CascadeV2Lag)
        )
    );
  }

  export const isSelectedGatewayAV2Cascade = createSelector(
    installationDetails,
    DevicesSelectors.getDevicesOfSelectedGateway(),
    (state, devicesWithFeatures: RichDevice[]): boolean | undefined => {
      if (!state.loaded || !state.installation) {
        return;
      }

      return devicesWithFeatures?.some(
        (device) =>
          device.roles.includes(DeviceRoles.CascadeV2Lead) ||
          device.roles.includes(DeviceRoles.CascadeV2Lag)
      );
    }
  );

  export function isSelectedGatewayV1Cascade(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.getDevicesOfSelectedGateway(),
      (devicesWithFeatures: RichDevice[]) =>
        devicesWithFeatures?.some((device) => device.roles.includes(DeviceRoles.CascadeLead))
    );
  }

  export function isServiceLayerVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationDetailsSelectors.isSelectedGatewayV1Cascade(),
      InstallationDetailsSelectors.isInstallationVitocrossalNAType(),
      (isV1Cascade: boolean, isVitocrossal: boolean) => isV1Cascade || isVitocrossal
    );
  }

  export function getDevice(): MemoizedSelector<
    AppState,
    DeviceDetails,
    DefaultProjectorFn<DeviceDetails>
  > {
    return createSelector(
      installationDetails,
      DevicesSelectors.getSelectedDevice,
      (_state: InstallationDetailsState, selectedDevice: RichDevice) => {
        const deviceDetails: DeviceDetails = {
          marketingName: selectedDevice?.marketingName,
          shortName: selectedDevice?.shortDeviceName,
          image: selectedDevice?.deviceImage,
          boilerSerial: selectedDevice?.boilerSerial,
          gatewaySerial: selectedDevice?.gatewaySerial,
          isLoading: selectedDevice?.loading,
          id: selectedDevice?.id,
        };

        return deviceDetails;
      }
    );
  }

  export function getSequencedCascadeLagDevices(): MemoizedSelector<
    AppState,
    CascadeLagDeviceDetails[],
    DefaultProjectorFn<CascadeLagDeviceDetails[]>
  > {
    return createSelector(
      InstallationFeaturesSelectors.installationFeatures,
      DevicesSelectors.getV1CascadeLeadDevice(),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getGatewaySerial(),
      DevicesSelectors.getSelectedDevice,
      (
        installationFeaturesState: InstallationFeaturesState,
        leadDevice: Device,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: string,
        stateDevice: RichDevice
      ) => {
        const cascadeMembersUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          FeatureNames.cascadeMembers,
          stateGatewaySerial,
          stateInstallationId,
          stateDevice?.id
        );
        const membersFeature = installationFeaturesState.entities[cascadeMembersUri] as Feature<
          CascadeMembersProperties,
          never
        >;
        const membersIds: number[] = membersFeature?.properties?.enabled?.value;
        const sequenceFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
          featuresBaseUri,
          FeatureNames.cascadeSequence,
          stateGatewaySerial,
          stateInstallationId,
          stateDevice?.id
        );
        const sequenceFeature: Feature<CascadeSequenceProperties, never> = installationFeaturesState
          .entities[sequenceFeatureUri] as Feature<CascadeSequenceProperties, never>;
        const sequenceIds: number[] = sequenceFeature?.properties.value?.value;

        const devicesIds: number[] = sequenceIds
          ?.map((id) => id)
          .filter((id) => membersIds?.includes(id) && id !== +leadDevice.id);

        if (devicesIds) {
          return devicesIds.map((deviceId) => {
            let id: string = deviceId.toString();

            const cascadeMemberByIdFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
              featuresBaseUri,
              FeatureNames.cascadeMember(id),
              stateGatewaySerial,
              stateInstallationId,
              leadDevice.id
            );

            const memberFeature: Feature<
              CascadeSquenceValuesProperties,
              CascadeSquenceValuesCommands
            > = installationFeaturesState.entities[cascadeMemberByIdFeatureUri] as Feature<
              CascadeSquenceValuesProperties,
              CascadeSquenceValuesCommands
            >;

            const burnerStatusFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
              featuresBaseUri,
              FeatureNames.cascadeLagDeviceBurnerStatus(id),
              stateGatewaySerial,
              stateInstallationId,
              leadDevice.id
            );

            const burnerStatusFeature: Feature<IsActiveProperty, never> = installationFeaturesState
              .entities[burnerStatusFeatureUri] as Feature<IsActiveProperty, never>;

            return {
              id,
              name: memberFeature.properties?.name?.value,
              type: memberFeature.properties?.type?.value,
              burnerStatus: burnerStatusFeature.properties?.active?.value,
            } as CascadeLagDeviceDetails;
          });
        } else {
          return [];
        }
      }
    );
  }

  export function getDeviceDisplayName(
    ownerInstalationOfTranslation: string,
    genericInstallationTranslation: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      installationDetails,
      DevicesSelectors.getSelectedDevice,
      (state: InstallationDetailsState, device: RichDevice) => {
        const deviceName = device?.marketingName;
        if (deviceName) {
          return deviceName;
        }

        const owner = state?.accessList?.owner as User;
        if (owner?.firstName && owner?.lastName) {
          return `${ownerInstalationOfTranslation} ${owner.firstName} ${owner.lastName}`;
        }

        return genericInstallationTranslation;
      }
    );
  }

  export function transferInstallationButtonVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      installationDetails,
      AuthSelectors.isRsaUser(),
      AuthSelectors.getMe(),
      (state: InstallationDetailsState, isRsaUser: boolean, me: MeUserDetails) => {
        return (
          state?.installation?.value?.ownershipType === OwnershipType.PreCommissioning &&
          !isRsaUser &&
          state.accessList.owner.email === me.email
        );
      }
    );
  }

  export function getAccessListCompanyName(
    invitationAccessLevel: InvitationAccessLevel
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(installationDetails, (state: InstallationDetailsState): string => {
      switch (invitationAccessLevel) {
        case InvitationAccessLevel.Operator: {
          return state.accessList.operator?.companyName;
        }
        case InvitationAccessLevel.ServiceContractor: {
          return state.accessList.serviceContractor?.companyName;
        }
      }
    });
  }

  export function getAccessListUserList(): MemoizedSelector<
    AppState,
    AccessListUserData[],
    DefaultProjectorFn<AccessListUserData[]>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) =>
      mapFromEntitiesToUserList(state?.accessList)
    );
  }

  export function getAccessListForCommercial(): MemoizedSelector<
    AppState,
    AccessListCommercialData,
    DefaultProjectorFn<AccessListCommercialData>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) => {
      return {
        companyOwnerNumber: state.accessList?.commercialOwner?.customerNumber,
        companyName: state.accessList?.commercialOwner?.companyName,
        companyAccounts:
          state.accountsByCompany[state.accessList?.commercialOwner?.customerNumber]?.map(
            (account) => {
              return {
                firstName: account.firstName,
                lastName: account.familyName,
                email: account.email,
                role: account.businessRole,
                viAccount: account.iamId,
              };
            }
          ) || [],
        partners: state.accessList?.partners?.map((partner) => {
          return {
            customerNumber: partner.customerNumber,
            accounts:
              state.accountsByCompany[partner.customerNumber]?.map((account) => {
                return {
                  firstName: account.firstName,
                  lastName: account.familyName,
                  email: account.email,
                  role: account.businessRole,
                  viAccount: account.iamId,
                };
              }) || [],
            companyName: partner.companyName,
          };
        }),
      };
    });
  }

  export function selectAccessListMaintainersList(): MemoizedSelector<
    AppState,
    MaintainerAccessAccount[],
    DefaultProjectorFn<MaintainerAccessAccount[]>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState): MaintainerAccessAccount[] => {
        return state.accessList.maintainers;
      }
    );
  }

  export function selectAccessListOwnerAndFamilyMembersList(): MemoizedSelector<
    AppState,
    User[],
    DefaultProjectorFn<User[]>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState): User[] => {
      return flatten([state.accessList.owner, state.accessList.familyMembers]).filter(Boolean);
    });
  }

  export function mapFromCustomerToUserTable(accounts: User[]): AccessListUserData[] {
    const users: AccessListUserData[] = accounts?.map((account) => {
      const accessListUserData: AccessListUserData = {
        firstName: account.firstName,
        lastName: account.lastName,
        email: account.email,
        role: account.role,
        viAccount: account.viAccount,
        id: account.id,
      };

      return accessListUserData;
    });

    return users;
  }

  export function getAddress(): MemoizedSelector<
    AppState,
    AddressDetails,
    DefaultProjectorFn<AddressDetails>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) => {
      const address = state?.installation?.value?.address;
      const addressDetails: AddressDetails = {
        street: address && `${address.street} ${address.houseNumber}`,
        city: address && `${address.zip} ${address.city}`,
        longitude: address?.geolocation?.longitude,
        latitude: address?.geolocation?.latitude,
      };

      return addressDetails;
    });
  }

  export function getOwner(): MemoizedSelector<
    AppState,
    OwnerDetails,
    DefaultProjectorFn<OwnerDetails>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) => {
      const owner = state?.accessList?.owner;
      const lastName = owner?.lastName ?? owner?.familyName;

      return {
        name: (owner && `${owner?.firstName} ${lastName}`) || '-',
        email: owner?.email || '-',
        phone: owner?.phoneNumber || owner?.mobile || '-',
        ownerId: owner?.id?.toString() || '',
      };
    });
  }

  export function getConsumerDetails(): MemoizedSelector<
    AppState,
    OwnerDetails,
    DefaultProjectorFn<OwnerDetails>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) => {
      const owner = state?.accessList?.consumers?.[0];

      if (owner) {
        return {
          name: owner && (`${owner?.firstName} ${owner?.familyName}` || '-'),
          email: owner?.email || '-',
          phone: owner?.mobile || '-',
          ownerId: owner?.id || '',
        };
      }

      return null;
    });
  }

  export function getGatewaySerial(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      InstallationDetailsSelectors.getSelectedGateway(),
      (selectedGateway: Gateway) => selectedGateway?.serial
    );
  }

  export function getGatewaysSerial(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) =>
      state?.installation?.value?.gateways?.map((gateway) => gateway.serial)
    );
  }

  export function getBoilerSerialEditableFlag(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.getSelectedDevice,
      (selectedDevice: RichDevice) => selectedDevice?.canEditBoilerSerial
    );
  }

  export function getInstallationTimezone(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) =>
        state?.installation?.value?.address?.geolocation?.timeZone
    );
  }

  export function getInstallationId(): MemoizedSelector<
    AppState,
    number,
    DefaultProjectorFn<number>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state?.installation?.value?.id
    );
  }

  export function getDepartmentName(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state?.installation?.value?.servicedBy
    );
  }

  export function getMaintenancePackageLink(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      DevicesSelectors.getSelectedDevice,
      (selectedDevice: RichDevice) => selectedDevice?.maintenancePackageLink
    );
  }

  export function getGatewayStatus(
    gatewaySerial?: string
  ): MemoizedSelector<AppState, GatewayStatus, DefaultProjectorFn<GatewayStatus>> {
    return createSelector(
      installationDetails,
      InstallationDetailsSelectors.getSelectedGateway(),
      (state: InstallationDetailsState, selectedGateway: Gateway) => {
        const currentGateway: Gateway = gatewaySerial
          ? state?.installation?.value?.gateways.find(
              (installationGateways) => installationGateways.serial === gatewaySerial
            )
          : selectedGateway;

        const gatewayStatus: GatewayStatus = {
          online:
            currentGateway?.aggregatedStatus === AggregatedStatus.WorksProperly ||
            currentGateway?.aggregatedStatus === AggregatedStatus.Error,
          offline: currentGateway?.aggregatedStatus === AggregatedStatus.Offline,
          error: currentGateway?.aggregatedStatus === AggregatedStatus.Error,
          maintenance: currentGateway?.aggregatedStatus === AggregatedStatus.Maintenance,
          remoteDiagnostic:
            currentGateway?.aggregatedStatus === AggregatedStatus.RemoteDiagnosticSession,
          worksProperly: currentGateway?.aggregatedStatus === AggregatedStatus.WorksProperly,
          nbiot: currentGateway?.aggregatedStatus === AggregatedStatus.NBIoT,
        };

        return gatewayStatus;
      }
    );
  }

  export function isNBIoT(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(getGatewayStatus(), (status: GatewayStatus) => status.nbiot);
  }

  /**
   * Type of Dashboard selected from installation details header
   * Does not define which view is displayed
   */
  export function getSelectedDashboardTypeHelper(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state.selectedDashboardTypeHelper
    );
  }

  /**
   * Aggregated type of Dashboard
   * Defines which view is displayed
   */
  export function getSelectedDashboardSlug(): MemoizedSelector<
    AppState,
    DashboardTypes,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      isNBIoT(),
      getSelectedDashboardTypeHelper(),
      DevicesSelectors.getSelectedDevice,
      (isNbiot, dashboardTypeHelper, selectedDevice) => {
        const isSolarLogSlug =
          isSolarLogInverter(selectedDevice) && dashboardTypeHelper === DashboardTypes.SOLARLOG;
        const isWallboxSlug =
          isWallboxGateway(selectedDevice) && dashboardTypeHelper === DashboardTypes.WALLBOX;
        const isHemsSlug = dashboardTypeHelper === DashboardTypes.HEMS;
        const isDryContactSlug = dashboardTypeHelper === DashboardTypes.DRY_CONTACT;
        const isSolarLogImmersionPvHeater =
          dashboardTypeHelper === DashboardTypes.PV_IMMERSION_HEATER;
        const isDryContactGenericConsumerSlug =
          dashboardTypeHelper === DashboardTypes.DRY_CONTACT_GENERIC_CONSUMER;

        if (isNbiot) {
          return DashboardTypes.NBIOT;
        } else if (isSolarLogSlug) {
          return DashboardTypes.SOLARLOG;
        } else if (isWallboxSlug) {
          return DashboardTypes.WALLBOX;
        } else if (isHemsSlug) {
          return DashboardTypes.HEMS;
        } else if (isSolarLogImmersionPvHeater) {
          return DashboardTypes.PV_IMMERSION_HEATER;
        } else if (isDryContactSlug) {
          return DashboardTypes.DRY_CONTACT;
        } else if (isDryContactGenericConsumerSlug) {
          return DashboardTypes.DRY_CONTACT_GENERIC_CONSUMER;
        }

        return DashboardTypes.DEFAULT;
      }
    );
  }

  export function isOTAOnGoing(
    gatewaySerial?: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      installationDetails,
      InstallationDetailsSelectors.getSelectedGateway(),
      (state: InstallationDetailsState, selectedGateway: Gateway) => {
        const currentGateway: Gateway = gatewaySerial
          ? state.installation?.value?.gateways.find(
              (installationGateways) => installationGateways.serial === gatewaySerial
            )
          : selectedGateway;

        return currentGateway?.otaOngoing;
      }
    );
  }

  // Used when installation details is active, for general usage getPlatformTypeWithInstallationId is recommended
  export function getMainDevicePlatform(): MemoizedSelector<
    AppState,
    Platform,
    DefaultProjectorFn<Platform>
  > {
    return createSelector(
      DevicesSelectors.getSelectedDevice,
      (device: RichDevice): Platform => {
        const platform: Platform = getDevicePlatformBasingOnRoles(device?.roles);

        return platform;
      }
    );
  }

  export function getMainDevicePlatformByGateway(
    gatewaySerial: string
  ): MemoizedSelector<AppState, Platform, DefaultProjectorFn<Platform>> {
    return createSelector(
      DevicesSelectors.selectAllDevices,
      (devices: RichDevice[]): Platform => {
        const mainDevice: RichDevice = devices.find(
          (dev) => dev.gatewaySerial === gatewaySerial && dev.isMainDeviceOfGateway
        );
        const platform = getDevicePlatformBasingOnRoles(mainDevice?.roles);

        return platform;
      }
    );
  }

  export const getPanelsOrder = createSelector(
    installationDetails,
    (state: InstallationDetailsState): InstallationDevicePanels => state.panelsOrder.value
  );

  export function getPanelsOrderForDevice(
    orderKey: string
  ): MemoizedSelector<
    AppState,
    InstallationPanelOrder[],
    DefaultProjectorFn<InstallationPanelOrder[]>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) => {
      if (state.panelsOrder.value) {
        if (!state.panelsOrder.value[orderKey]) {
          return [];
        }

        return state.panelsOrder.value[orderKey];
      }
    });
  }

  export function getMaintenanceChecklist(): MemoizedSelector<
    AppState,
    EquipmentDocumentResponse[],
    DefaultProjectorFn<EquipmentDocumentResponse[]>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state.maintenanceChecklist.value
    );
  }

  export function getServiceInstruction(): MemoizedSelector<
    AppState,
    EquipmentDocumentResponse[],
    DefaultProjectorFn<EquipmentDocumentResponse[]>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state.serviceInstruction.value
    );
  }

  export function selectIsServiceInstructionLoading(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state.serviceInstruction.loading
    );
  }

  export function isInstallationLegacyHP(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.getSelectedDevice,
      (device: RichDevice) =>
        hasMainDeviceRole(device) &&
        device.roles.includes(DeviceRoles.Heatpump) &&
        device.roles.includes(DeviceRoles.Legacy)
    );
  }

  export function isInstallationVitocrossalNAType(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.getSelectedDevice,
      (device: RichDevice) =>
        hasMainDeviceRole(device) && device.roles.includes(DeviceRoles.ProductVitocrossalNA)
    );
  }

  export function isMainDeviceVitocharge(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.getSelectedDevice,
      (device: RichDevice) =>
        hasMainDeviceRole(device) && device.roles.includes(DeviceRoles.ProductVitocharge)
    );
  }

  export function isMainDeviceB2C(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.getSelectedDevice,
      (device: RichDevice) => hasMainDeviceRole(device) && device.roles.includes(DeviceRoles.B2C)
    );
  }

  export function isFirmwareUpToDate(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      getSelectedGateway(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature<
        GatewayFirmwareProperties,
        GatewayFirmwareCommands
      >(FeatureNames.gatewayFirmware),
      (
        gateway: Gateway,
        gatewayFirmware: Feature<GatewayFirmwareProperties, GatewayFirmwareCommands>
      ) => {
        const gatewayType = gateway?.gatewayType;
        const version = gatewayFirmware?.properties?.version?.value;

        if (!version) {
          return true;
        }

        const parsedCurrentVersion = parseVersion(version);

        if (gatewayType in GatewayTypeLegacy) {
          const [_currentMajor, currentMinor, currentPatch] = parsedCurrentVersion;
          const expectedVersion = AzureCertificateValidVersions[gatewayType];

          if (!expectedVersion) {
            return true;
          }

          const [_, expectedMinor, expectedPatch] = parseVersion(expectedVersion);

          return (
            expectedMinor < currentMinor ||
            (expectedMinor === currentMinor && expectedPatch <= currentPatch)
          );
        } else {
          const [_currentfeatureSet, currentCalendarWeek] = parsedCurrentVersion;
          const expectedVersion = AzureCertificateValidVersions[gatewayType];

          if (!expectedVersion) {
            return true;
          }

          const [_featureSet, expectedCalendarWeek] = parseVersion(expectedVersion);

          return expectedCalendarWeek <= currentCalendarWeek;
        }
      }
    );
  }

  export function mapFromEntitiesToUserList(payload: AccessList): AccessListUserData[] {
    const users: AccessListUserData[] = [];
    const accountEntities: (User | Account)[] = flatten<User | Account>([
      payload.owner,
      payload.familyMembers,
      payload.buildingManager?.accounts,
      payload.operator?.accounts,
      payload.serviceContractor?.accounts,
    ]).filter(Boolean);

    accountEntities.forEach((account) => {
      const accessListUserData: AccessListUserData = {
        firstName: account?.firstName,
        lastName: account?.lastName,
        email: account?.email,
        role: account?.role,
        viAccount: account?.viAccount,
        id: account?.id,
      };

      users.push(accessListUserData);
    });

    return users;
  }

  // menu bar selectors
  export function permanentInspectionMenuItemVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      AuthSelectors.isRsaUser(),
      InstallationDetailsSelectors.getAvailablePaidFeatures(),
      InstallationDetailsSelectors.isNBIoT(),
      AuthSelectors.isMaintainer,
      DevicesSelectors.isE3HeatpumpDevice(),
      (
        isRsa: boolean,
        availablePaidFeatures: string[],
        nbiot: boolean,
        isMaintainer: boolean,
        isE3HeatpumpDevice: boolean
      ) =>
        ((isRsa ||
          isPaidFeatureAvailable(availablePaidFeatures, PaidFeaturesEnum.PermanentInspection)) &&
          !nbiot) ||
        (isMaintainer && isE3HeatpumpDevice)
    );
  }

  export function isPartnerAccessMenuVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      AuthSelectors.selectAvailableGlobalPaidFeaturePresence(PaidFeature.UserManagement),
      AuthSelectors.isRsaUser(),
      AuthSelectors.getCompanyId(),
      InstallationDetailsSelectors.selectOwnershipType,
      InstallationDetailsSelectors.getAccessListForCommercial(),
      (
        isUserManagement: boolean,
        isRsaUser: boolean,
        companyId: string,
        selectOwnershipType: OwnershipType,
        accessListCommercialData: AccessListCommercialData
      ) =>
        selectOwnershipType === OwnershipType.CommercialV2 &&
        (isRsaUser ||
          (isUserManagement && companyId === accessListCommercialData.companyOwnerNumber))
    );
  }

  export function backupMenuItemVisible(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationDetailsSelectors.getGatewayStatus(),
      AuthSelectors.hasUserRolesForRemoteDiagnosis(),
      InstallationDetailsSelectors.getAvailablePaidFeatures(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics,
        gatewaySerial
      ),
      isMainDeviceE3(gatewaySerial),
      DevicesSelectors.isEcoWaterDevice,
      InstallationDetailsSelectors.hasSelectedDeviceGivenRoles([DeviceRoles.BackupCapability]),
      InstallationDetailsSelectors.isSelectedGatewayAV2Cascade,
      RemoteDiagnosticsSelectors.getMainLogicalLinkFromTopology(gatewaySerial),
      (
        gatewayStatus: GatewayStatus,
        hasRSADiagnosticsRole: boolean,
        availablePaidFeatures: string[],
        feature: Feature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>,
        isE3: boolean,
        isEcoWaterDevice: boolean,
        hasBackupRole: boolean,
        isGatewayAV2Cascade: boolean,
        mainLogicalLinkFromTopology: string
      ) =>
        !gatewayStatus.offline &&
        isReady(feature) &&
        isE3 &&
        !isEcoWaterDevice &&
        !isGatewayAV2Cascade &&
        hasBackupRole &&
        !!mainLogicalLinkFromTopology &&
        (hasRSADiagnosticsRole ||
          isPaidFeatureAvailable(availablePaidFeatures, PaidFeaturesEnum.SystemOverview))
    );
  }

  export function shouldFetchEnergyConsumptionDataForLegacyHeatpumps(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      DevicesSelectors.isEcotronicDevice,
      InstallationDetailsSelectors.isInstallationLegacyHP(),
      RemoteConfigSelectors.shouldFetchEnergyConsumptionDataForLegacyHeatpump(),
      (isEcotronicDevice: boolean, isLegacyHP: boolean, shouldFetchData: boolean) => {
        return (isLegacyHP || isEcotronicDevice) && shouldFetchData;
      }
    );
  }

  export function snapshotMenuItemVisible(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationDetailsSelectors.getGatewayStatus(),
      AuthSelectors.hasUserRolesForRemoteDiagnosis(),
      InstallationDetailsSelectors.getAvailablePaidFeatures(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics,
        gatewaySerial
      ),
      InstallationDetailsSelectors.hasSelectedDeviceGivenRoles([DeviceRoles.BackupCapability]),
      isMainDeviceE3(gatewaySerial),
      DevicesSelectors.isEcoWaterDevice,
      (
        gatewayStatus: GatewayStatus,
        hasRSADiagnosticsRole: boolean,
        availablePaidFeatures: string[],
        feature: Feature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>,
        hasBackupCapabilityRole,
        isE3: boolean,
        isEcoWaterDevice: boolean
      ) =>
        !gatewayStatus.offline &&
        isReady(feature) &&
        isE3 &&
        !isEcoWaterDevice &&
        hasBackupCapabilityRole &&
        (hasRSADiagnosticsRole ||
          isPaidFeatureAvailable(availablePaidFeatures, PaidFeaturesEnum.SystemOverview))
    );
  }

  export function extendedParametersMenuItemVisible(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationDetailsSelectors.getGatewayStatus(),
      AuthSelectors.hasUserRolesForRemoteDiagnosis(),
      InstallationDetailsSelectors.getAvailablePaidFeatures(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics,
        gatewaySerial
      ),
      isMainDeviceE3(gatewaySerial),
      DevicesSelectors.isEcoWaterDevice,
      (
        gatewayStatus: GatewayStatus,
        hasRSADiagnosticsRole: boolean,
        availablePaidFeatures: string[],
        feature: Feature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>,
        isE3: Boolean,
        isEcoWaterDevice: boolean
      ) =>
        !gatewayStatus.offline &&
        isReady(feature) &&
        isE3 &&
        !isEcoWaterDevice &&
        (hasRSADiagnosticsRole ||
          isPaidFeatureAvailable(availablePaidFeatures, PaidFeaturesEnum.ExtendedParameters))
    );
  }

  export function selectIsServiceLayerFeatureAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.ventilationSensorsActuatorSelftest
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.ventilationFilterInformation
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.ventilationLockExternal
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.ventilationFanAssignmentSwitch
      ),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.ventilationSwitchActivation
      ),
      (
        ventilationSensorsActuatorSelftestFeature: Feature<
          VentilationSensorsActuatorSelftestProperties,
          never
        >,
        ventilationFilterInformation: Feature<VentilationFilterInformationProperties, never>,
        ventilationLockExternal: Feature<VentilationLockExternalProperties, never>,
        ventilationFanAssignmentSwitch: Feature<IsActiveProperty, never>,
        ventilationSwitchActivation: Feature<IsActiveProperty, never>
      ) => {
        return (
          isReady(ventilationSensorsActuatorSelftestFeature) ||
          isReady(ventilationFilterInformation) ||
          isReady(ventilationLockExternal) ||
          isReady(ventilationFanAssignmentSwitch) ||
          isReady(ventilationSwitchActivation)
        );
      }
    );
  }

  export function selectIsServiceLayerButtonVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationDetailsSelectors.getGatewayStatus(),
      InstallationDetailsSelectors.selectIsServiceLayerFeatureAvailable(),
      DevicesSelectors.selectIsSelectedDeviceVentilationDevice,
      (
        gatewayStatus: GatewayStatus,
        isServiceLayerFeatureAvailable: boolean,
        isVentilationDevice: boolean
      ) => {
        return !gatewayStatus.offline && isServiceLayerFeatureAvailable && isVentilationDevice;
      }
    );
  }

  export function trendingMenuItemVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      AuthSelectors.isRsaUser(),
      DevicesSelectors.isStandaloneDevice,
      DevicesSelectors.isEcoWaterDevice,
      InstallationDetailsSelectors.getAvailablePaidFeatures(),
      InstallationDetailsSelectors.trendingFeaturesOnDevices(),
      (
        isRsaUser: boolean,
        isStandalone: boolean,
        isEcoWaterDevice: boolean,
        availablePaidFeatures: string[],
        trendingPropertiesOnDevice: string[]
      ) => {
        return (
          !isStandalone &&
          !isEcoWaterDevice &&
          (isRsaUser ||
            isPaidFeatureAvailable(
              availablePaidFeatures,
              PaidFeaturesEnum.DataRecorderAndDataTrending
            )) &&
          trendingPropertiesOnDevice.length > 0
        );
      }
    );
  }

  export function trendingFeaturesOnDevices(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      DevicesSelectors.selectTrendingDevices,
      InstallationFeaturesSelectors.installationFeatures,
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getGatewaySerial(),
      DevicesSelectors.selectFeatureForEnergyStorageDataTrending(),
      (
        trendingDevices: RichDevice[],
        installationFeaturesState: InstallationFeaturesState,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: string,
        energyStorageDataTrending: string[]
      ) => {
        const presentFeatures = [];
        const allTrendingProperties: string[] = [
          ...trendingProperties,
          ...energyStorageDataTrending,
          ...communicationModuleProperties,
        ];

        const lastSegmentRegex = /\/([^\/]+)$/;
        const dlRegex = /^dl\./;

        trendingDevices.forEach((trendingDevice) => {
          allTrendingProperties.forEach((featureEntry) => {
            // removes last segment or first dl segment of feature eg
            // feature.one/value -> feature.one
            // feature.two/level1 -> feature.two
            // dl.feature.two/level1 -> feature.two
            const featureWithoutValueSuffix = featureEntry
              .replace(lastSegmentRegex, '')
              .replace(dlRegex, '');

            const trendingPropertyUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
              featuresBaseUri,
              featureWithoutValueSuffix,
              stateGatewaySerial,
              stateInstallationId,
              trendingDevice?.id
            );

            const trendingFeature = installationFeaturesState?.entities[
              trendingPropertyUri
            ] as Feature<GenericNumberValueProperty, never>;

            if (trendingFeature && !presentFeatures.includes(featureEntry)) {
              presentFeatures.push(featureEntry);
            }
          });
        });

        // Check for gateway-level properties (e.g. gateway.wifi, gateway.status)
        allTrendingProperties.forEach((featureEntry) => {
          if (presentFeatures.includes(featureEntry)) {
            return;
          }

          const featureWithoutValueSuffix = featureEntry
            .replace(lastSegmentRegex, '')
            .replace(dlRegex, '');

          const gatewayFeatureUri = InstallationFeaturesHelpers.constructInstallationFeatureUri(
            featuresBaseUri,
            featureWithoutValueSuffix,
            stateGatewaySerial,
            stateInstallationId
          );

          const gatewayFeature = installationFeaturesState?.entities[gatewayFeatureUri] as Feature<
            GenericNumberValueProperty,
            never
          >;

          if (gatewayFeature) {
            presentFeatures.push(featureEntry);
          }
        });

        return presentFeatures;
      }
    );
  }

  export function diagnosisMenuVisible(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationDetailsSelectors.trendingMenuItemVisible(),
      InstallationDetailsSelectors.extendedParametersMenuItemVisible(gatewaySerial),
      InstallationDetailsSelectors.snapshotMenuItemVisible(gatewaySerial),
      InstallationDetailsSelectors.backupMenuItemVisible(gatewaySerial),
      InstallationDetailsSelectors.permanentInspectionMenuItemVisible(),
      InstallationDetailsSelectors.isNBIoT(),
      InstallationDetailsSelectors.selectGatewayHasDevices(gatewaySerial),
      (
        trendingVisible,
        extendedParametersVisible,
        snapshotVisible,
        backupVisible,
        permanentInspectionVisible,
        nbiot,
        hasDevices
      ) =>
        hasDevices &&
        !nbiot &&
        (trendingVisible ||
          extendedParametersVisible ||
          snapshotVisible ||
          backupVisible ||
          permanentInspectionVisible)
    );
  }

  export function isDiagnosticsAvailable(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationDetailsSelectors.extendedParametersMenuItemVisible(gatewaySerial),
      InstallationDetailsSelectors.snapshotMenuItemVisible(gatewaySerial),
      InstallationDetailsSelectors.backupMenuItemVisible(gatewaySerial),
      InstallationDetailsSelectors.hydraulicSchemeMenuItemVisible(gatewaySerial),
      InstallationDetailsSelectors.isGuidedTroubleshootingAvailable(gatewaySerial),
      (
        extendedParametersVisible,
        snapshotVisible,
        backupVisible,
        hydraulicSchemeVisible,
        isGtsAvailable
      ) =>
        extendedParametersVisible ||
        snapshotVisible ||
        backupVisible ||
        hydraulicSchemeVisible ||
        isGtsAvailable ||
        true
    );
  }

  export function maintenanceChecklistMenuItemVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      AuthSelectors.isMaintainerOrUser(),
      DevicesSelectors.isEcoWaterDevice,
      InstallationDetailsSelectors.getAvailablePaidFeatures(),
      (isMaintainerOrUser: boolean, isEcoWaterDevice: boolean, availablePaidFeatures: string[]) =>
        isMaintainerOrUser &&
        !isEcoWaterDevice &&
        isPaidFeatureAvailable(availablePaidFeatures, PaidFeaturesEnum.SystemOverview)
    );
  }

  export function serviceInstructionMenuItemVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      AuthSelectors.isRsaOrMaintainer,
      DevicesSelectors.isEcoWaterDevice,
      InstallationDetailsSelectors.getDeviceSerialForRemoteDiagnostics(),
      (isRsaOrMaintainer: boolean, isEcoWaterDevice: boolean, boilerSerial: string) =>
        isRsaOrMaintainer && !isEcoWaterDevice && !!boilerSerial
    );
  }

  export function isGuidedTroubleshootingAvailable(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      AuthSelectors.hasUserRolesForRemoteDiagnosis(),
      InstallationDetailsSelectors.getAvailablePaidFeatures(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics,
        gatewaySerial
      ),
      isMainDeviceE3(gatewaySerial),
      DevicesSelectors.isEcoWaterDevice,
      (
        hasRSADiagnosticsRole: boolean,
        availablePaidFeatures: string[],
        feature: Feature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>,
        isE3: boolean,
        isEcoWaterDevice: boolean
      ) =>
        isReady(feature) &&
        isE3 &&
        !isEcoWaterDevice &&
        (hasRSADiagnosticsRole ||
          isPaidFeatureAvailable(availablePaidFeatures, PaidFeaturesEnum.GuidedTroubleshooting))
    );
  }

  export function hydraulicSchemeMenuItemVisible(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      AuthSelectors.hasUserRolesForRemoteDiagnosis(),
      InstallationDetailsSelectors.getAvailablePaidFeatures(),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics,
        gatewaySerial
      ),
      isMainDeviceE3(gatewaySerial),
      DevicesSelectors.isEcoWaterDevice,
      InstallationDetailsSelectors.isInstallationVitocrossalNAType(),
      InstallationDetailsSelectors.isMainDeviceVitocharge(),
      InstallationDetailsSelectors.isSelectedGatewayAV2Cascade,
      // TODO VIG-5255 required until we extend all tabs to support multiple devices for gateway ( not ready due to backend )
      (
        hasRSADiagnosticsRole: boolean,
        availablePaidFeatures: string[],
        feature: Feature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>,
        isE3: boolean,
        isEcoWaterDevice: boolean,
        isVitocrossalType: boolean,
        isVitocharge: boolean,
        selectedGatewayIsAV2Cascade: boolean
      ) =>
        !isVitocrossalType &&
        !isVitocharge &&
        isReady(feature) &&
        isE3 &&
        !isEcoWaterDevice &&
        !selectedGatewayIsAV2Cascade &&
        (hasRSADiagnosticsRole ||
          isPaidFeatureAvailable(availablePaidFeatures, PaidFeaturesEnum.SystemOverview))
    );
  }

  export function selectInstallationSubscriptionPlan(
    boilerSerial: string
  ): MemoizedSelector<AppState, SubscriptionPlan, DefaultProjectorFn<SubscriptionPlan>> {
    return createSelector(
      installationDetails,
      InstallationsSelectors.getInstallationPackage(boilerSerial),
      AuthSelectors.selectCompanySubscriptionPlan(),
      (
        state: InstallationDetailsState,
        installationPackage: InstallationPackageStatus,
        companySubscriptionPlan: SubscriptionPlan
      ) => {
        if (!state.subscriptionPlanLoading) {
          return state.subscriptionPlan || installationPackage?.plan || companySubscriptionPlan;
        }

        return null;
      }
    );
  }

  export function getBatteryNameBySerial(serialNumber: string): string {
    const serialFirst7Digits = serialNumber?.substring(0, 7);
    const nameBySerial: { [index: string]: string } = {
      7727520: 'BM Typ 2.0A / CBAK 3.4Ah',
      7377123: 'BM Typ 2.4A / CBAK 3.6Ah',
      7377124: 'BM Typ 2.4B / BST 3.6Ah',
    };

    return nameBySerial[serialFirst7Digits];
  }

  export function isInstallationDetailsLoadingIndicator(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      getLoadedIndicator(),
      DevicesSelectors.getIndicators(),
      InstallationFeaturesSelectors.selectFeaturesLoading,
      (
        isInstallationDetailsLoaded: boolean,
        deviceIndicator: Indicator,
        selectFeaturesLoading: boolean
      ): boolean =>
        isInstallationDetailsLoaded && !deviceIndicator?.loading && !selectFeaturesLoading
    );
  }

  export function isMainDeviceE3(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      getMainDevicePlatformByGateway(gatewaySerial),
      (platform): boolean => platform === Platform.E3
    );
  }

  export function hasSelectedDeviceGivenRoles(
    roles: DeviceRoles[]
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(DevicesSelectors.getSelectedDevice, (device: RichDevice): boolean => {
      return device?.roles.some((deviceRole) => roles.every((role) => deviceRole.includes(role)));
    });
  }

  export function hasRoomControlGivenRoles(
    roles: DeviceRoles[]
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(DevicesSelectors.devicesState, (devicesState: DevicesState): boolean => {
      const roomControl: RichDevice = Object.values(devicesState.devices.entities).find(
        (device) => device.deviceType === DeviceTypes.RoomControl
      );

      if (roomControl) {
        return roomControl.roles.some((roomControlRole) =>
          roles.every((role) => roomControlRole.includes(role))
        );
      }

      return false;
    });
  }

  export function isPartnerShopLinkAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      AuthSelectors.getVkorg(),
      AuthSelectors.isMaintainerOrUser(),
      (vkOrg: string, isMaintainer: boolean): boolean => {
        const partnerShopLinkAvailable = partnerShopLinkVkOrgs.some((partnerShopLinkVkorg) =>
          vkOrg?.includes(partnerShopLinkVkorg)
        );

        return partnerShopLinkAvailable && isMaintainer;
      }
    );
  }

  export function showActiveErrorsForE3(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      getAvailablePaidFeatures(),
      AuthSelectors.isRsaUser(),
      RemoteDiagnosticsSelectors.isActiveSessionAvailable(gatewaySerial),
      (
        availableFeatures: string[],
        isRsaUser: boolean,
        isActiveSessionAvailable: boolean
      ): boolean =>
        (isPaidFeatureAvailable(availableFeatures, PaidFeaturesEnum.GuidedTroubleshooting) &&
          isRsaUser) ||
        isActiveSessionAvailable
    );
  }

  export function isMaintainerAssigned(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => !!state.accessList?.maintainers?.length
    );
  }
  export const selectIsAnyConsumerInAccessList = createSelector(
    installationDetails,
    (state: InstallationDetailsState): boolean => !!state.accessList?.consumers?.length
  );

  export function isPlusOrProPackage(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationsSelectors.getInstallationPackage(boilerSerial),
      (installationPackage): boolean =>
        installationPackage?.plan === SubscriptionPlan.Plus ||
        installationPackage?.plan === SubscriptionPlan.Pro
    );
  }

  export function getDeviceSerialForRemoteDiagnostics(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      InstallationDetailsSelectors.isSelectedGatewayV1Cascade(),
      InstallationDetailsSelectors.isSelectedGatewayAV2Cascade,
      DevicesSelectors.getDevicesOfV2Cascade(),
      DevicesSelectors.getV1CascadeLeadDevice(),
      DevicesSelectors.getCurrentlySelectedDevice(),

      (
        isSelectedGatewayV1CascadeInner: boolean,
        isSelectedGatewayAV2CascadeInner: boolean,
        v2CascadeDevices: RichDevice[],
        cascadeLeadDevice: RichDevice,
        selectedDevice: RichDevice
      ): string => {
        if (isSelectedGatewayV1CascadeInner) {
          return cascadeLeadDevice?.boilerSerial;
        }
        if (isSelectedGatewayAV2CascadeInner) {
          return v2CascadeDevices[0]?.boilerSerial;
        }

        return selectedDevice?.boilerSerial;
      }
    );
  }

  export function getSpecificGatewayData(
    gatewaySerial
  ): MemoizedSelector<AppState, Gateway, DefaultProjectorFn<Gateway>> {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState): Gateway | null =>
        state.installation.value.gateways.find((gateway) => gateway.serial === gatewaySerial) ??
        null
    );
  }

  export function getInstallationBuildingInfo(): MemoizedSelector<
    AppState,
    BuildingInfo,
    DefaultProjectorFn<BuildingInfo>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState): BuildingInfo => {
        return {
          buildingName: state.installation?.value?.buildingName,
          buildingEmail: state.installation?.value?.buildingEmail,
          buildingPhone: state.installation?.value?.buildingPhone,
        };
      }
    );
  }

  export const getInstallationAddressInfo = createSelector(
    installationDetails,
    (state: InstallationDetailsState): Address => state?.installation?.value?.address
  );

  export function isProPackageOrRsa(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationsSelectors.getInstallationPackage(boilerSerial),
      AuthSelectors.isRsaUser(),
      (installationPackage: InstallationPackageStatus, isRsa: boolean): boolean => {
        return isRsa || installationPackage?.plan === SubscriptionPlan.Pro;
      }
    );
  }

  export function isMaintainerWithFreeOrBasicOrPlusPackage(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationsSelectors.getInstallationSubscriptionDetails(boilerSerial),
      AuthSelectors.isMaintainer,
      (installationPackage: InstallationPackageStatus, isMaintainer: boolean): boolean => {
        return (
          isMaintainer &&
          (installationPackage?.plan === SubscriptionPlan.Free ||
            installationPackage?.plan === SubscriptionPlan.Basic ||
            installationPackage?.plan === SubscriptionPlan.Plus)
        );
      }
    );
  }

  export function isPackageFetched(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationsSelectors.getInstallationPackage(boilerSerial),
      (installationPackage: InstallationPackageStatus): boolean => !!installationPackage?.plan
    );
  }

  export const getViCarePlans = createSelector(
    installationDetails,
    (state: InstallationDetailsState) => state.vicarePlans?.value
  );

  export const getViCarePlansFilteredByMonetizationFeatures = createSelector(
    installationDetails,
    DevicesSelectors.getSelectedDevice,
    (
      { monetizationFeatures, vicarePlans }: InstallationDetailsState,
      selectedDevice: RichDevice
    ): ViCarePlan<MonetizationFeature>[] => {
      const mappedMonetizationFeatures = InstallationDetailsMappers.mapMonetizationFeaturesResponse(
        monetizationFeatures?.value,
        selectedDevice
      );

      return vicarePlans.value.map((plan) => ({
        ...plan,
        features: plan.features
          .map((feature) => ({
            ...(mappedMonetizationFeatures[feature] ?? {
              name: feature,
              reason: '',
              supported: true,
            }),
          }))
          .sort((feature1, feature2) => Number(feature2.supported) - Number(feature1.supported)),
      }));
    }
  );

  export const getMaintenanceChecklistStatus = createSelector(
    installationDetails,
    ({ maintenanceChecklist }: InstallationDetailsState): GenericStoreStatus => ({
      error: maintenanceChecklist.error,
      loaded: maintenanceChecklist.loaded,
      loading: maintenanceChecklist.loading,
    })
  );

  export const isMainDeviceHeatPump = createSelector(
    DevicesSelectors.getSelectedDevice,
    (device: RichDevice) => hasMainDeviceRole(device) && device.roles.includes(DeviceRoles.Heatpump)
  );

  export const isMainDeviceBoiler = createSelector(
    DevicesSelectors.getSelectedDevice,
    (device: RichDevice) => hasMainDeviceRole(device) && device.roles.includes(DeviceRoles.Boiler)
  );

  export const isPermamentInspectionFeatureSupported = createSelector(
    installationDetails,
    (state: InstallationDetailsState) => state.isPermamentInspectionFeatureSupported
  );

  export const selectOwnershipType = createSelector(
    installationDetails,
    (state: InstallationDetailsState): OwnershipType => state.installation?.value?.ownershipType
  );

  // function wrapping forced by circular dependency
  export function isCancelMonitoringButtonVisible(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationDetailsSelectors.selectOwnershipType,
      AuthSelectors.userHasAccessToInvitations(),
      AuthSelectors.isCommercialUser,
      AuthSelectors.isMaintainer,
      AuthSelectors.selectIsCompanyIdIsSameAsCommercialOwner(),
      (
        ownershipType: OwnershipType,
        userHasAccessToInvitations: boolean,
        isCommercialUser: boolean,
        isMaintainer: boolean,
        isCompanyIdIsSameAsCommercialOwner: boolean
      ): boolean => {
        // Button should not be visible for maintainers from commercialOwner company
        const commercialCommissioningCheck = isMaintainer
          ? !isCompanyIdIsSameAsCommercialOwner
          : false;

        return (
          ownershipType !== OwnershipType.Oma &&
          ownershipType !== OwnershipType.PreCommissioning &&
          ownershipType !== OwnershipType.CommercialV1 &&
          userHasAccessToInvitations &&
          !isCommercialUser &&
          commercialCommissioningCheck
        );
      }
    );
  }

  export const selectInstallationWithCommercialOwner = createSelector(
    installationDetails,
    (state): boolean => state.installation?.value?.ownershipType === OwnershipType.CommercialV2
  );

  export const selectIsAccessListDefined = createSelector(
    installationDetails,
    (state): boolean => !!state.accessList
  );

  export const selectConsumerAddress = createSelector(
    installationDetails,
    (_state): AddressDetails => {
      //Follow ticket VIG-18204: UserV3 doesn't have address so I temporary comment it until we have infomation about this
      // const address = state.accessList?.consumers?.[0]?.address;
      // const addressDetails: AddressDetails = {
      //   street: address && `${address.street} ${address.houseNumber}`,
      //   city: address && `${address.zip} ${address.city}`,
      //   longitude: address?.geolocation?.longitude,
      //   latitude: address?.geolocation?.latitude,
      // };

      const addressDetails: AddressDetails = {
        street: null,
        city: null,
        longitude: null,
        latitude: null,
      };

      return addressDetails;
    }
  );

  export function shouldDisplayBookedPackageCardBadgeForResidential(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      getGatewayStatus(),
      BookingSelectors.hasAccessToBooking(),
      BookingSelectors.subscriptionsLoaded,
      InstallationsSelectors.getInstallationSubscriptionDetails(boilerSerial),
      (gatewayStatus, hasAccessToBooking, subscriptionsLoaded, subscription): boolean =>
        (!gatewayStatus.offline || hasAccessToBooking) && subscriptionsLoaded && !!subscription
    );
  }

  export const selectInstallationType = createSelector(
    installationDetails,
    (state): InstallationType => {
      return state.installation?.value?.installationType;
    }
  );

  // TODO remove in VIG-12178 cleanup
  export function isEditBoilerSerialIconVisible(
    device: RichDevice
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      DevicesSelectors.getCurrentlySelectedDevice(),
      AuthSelectors.isAdminOrSupportInternational(),
      getBoilerSerialEditableFlag(),
      RemoteConfigSelectors.isBoilerSerialEditable,
      (
        selectedDevice,
        isAdminOrSupport,
        canEditBoilerSerial,
        remoteConfigFlagIsBoilerSerialEditable
      ): boolean =>
        remoteConfigFlagIsBoilerSerialEditable
          ? device.isBoilerSerialEditable
          : !device.isStandalone &&
            selectedDevice?.boilerSerial === device.boilerSerial &&
            (isAdminOrSupport || canEditBoilerSerial)
    );
  }

  export function selectGatewayHasDevices(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(installationDetails, (state): boolean => {
      return (
        state.installation?.value?.gateways?.find((gateway) => gateway?.serial === gatewaySerial)
          ?.devices?.length > 0
      );
    });
  }

  export function getDeregisterGatewayState(): MemoizedSelector<
    AppState,
    any,
    DefaultProjectorFn<any>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state.deregisterGateway
    );
  }

  export const selectSubscriptionPlanLoading = createSelector(
    installationDetails,
    (state: InstallationDetailsState): boolean => state.subscriptionPlanLoading
  );

  export function hemsAdvancedAnalysisStatus(): MemoizedSelector<
    AppState,
    HemsAdvancedAnalysisStatus,
    DefaultProjectorFn<HemsAdvancedAnalysisStatus>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationHemsFeature<EmsPowerBalanceProperties, never>(
        FeatureNames.emsPowerBalance
      ),
      (powerBalance: Feature<EmsPowerBalanceProperties, never>): HemsAdvancedAnalysisStatus => {
        if (!!powerBalance) {
          return HemsAdvancedAnalysisStatus.DisplayAnalysis;
        } else {
          return HemsAdvancedAnalysisStatus.HemsNotActivated;
        }
      }
    );
  }

  export function selectIsPermanentInspectionEntriesVisible(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      getGatewayStatus(gatewaySerial),
      RemoteServiceSelectors.getDiagnosisStatus(),
      (gatewayStatus: GatewayStatus, diagnosisStatus: LoadingStatus) => {
        const correctGatewayStatus = !!gatewayStatus && !gatewayStatus.offline;
        const correctRemoteServiceData = diagnosisStatus.dataPresent && diagnosisStatus.loaded;

        return correctGatewayStatus && correctRemoteServiceData;
      }
    );
  }

  export function getHemsGateway(): MemoizedSelector<
    AppState,
    Gateway,
    DefaultProjectorFn<Gateway>
  > {
    return createSelector(
      installationDetails,
      HemsSelectors.selectHemsGatewaySerialIfNoError,
      InstallationDetailsSelectors.getSelectedGateway(),
      (state: InstallationDetailsState, hemsGatewaySerial: string, selectedGateway: Gateway) => {
        if (!hemsGatewaySerial) {
          return selectedGateway;
        }

        return state?.installation?.value?.gateways?.find(
          (gateway) => gateway?.serial === hemsGatewaySerial
        );
      }
    );
  }

  export function getFeaturePowerLimitationOverview(
    limitationType: PowerLimitationType
  ): MemoizedSelector<
    AppState,
    FeaturePowerLimitationOverview,
    DefaultProjectorFn<FeaturePowerLimitationOverview>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.powerLimitationOverview
      ),
      (
        powerLimitationOverview: Feature<PowerLimitationOverviewProperties, never>
      ): FeaturePowerLimitationOverview => {
        const featurePowerLimitationOverview = powerLimitationOverview?.properties?.limitations?.value?.find(
          (value) =>
            value.method === PowerLimitationMethod.hems && value.limitationType === limitationType
        );

        return featurePowerLimitationOverview;
      }
    );
  }

  export function selectIsPowerLimitationHistoryLogMenuItemVisible(
    device: RichDevice
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      DevicesSelectors.getDevicesIdsOfVitoCharge(),
      DevicesSelectors.getWallboxDevicesOfSelectedDevice,
      InstallationDetailsSelectors.getInstallationAddressInfo,
      RemoteConfigSelectors.selectPowerLimitationsEventLogVisible,
      DevicesSelectors.isE3HeatpumpDevice(device),
      DevicesSelectors.getDeviceIdOfOBNGOutdoorUnits(),
      (
        devicesIdsOfVitoCharge: string[],
        wallboxDevicesOfSelectedDevice: RichDevice[],
        address: Address,
        allowedCountries: string[],
        isE3HeatpumpDevice: boolean,
        deviceIdOfOBNGOutdoorUnits: string[]
      ): boolean => {
        const isDevicePresent =
          !!devicesIdsOfVitoCharge.length ||
          !!wallboxDevicesOfSelectedDevice.length ||
          !!deviceIdOfOBNGOutdoorUnits.length;
        const isCountryAllowed = allowedCountries.includes(address?.country.toLowerCase());

        return isCountryAllowed && (isDevicePresent || isE3HeatpumpDevice);
      }
    );
  }

  export function getAccountsByCompany(
    companyNumber: string
  ): MemoizedSelector<AppState, CompanyUser[], DefaultProjectorFn<CompanyUser[]>> {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state.accountsByCompany[companyNumber] || null
    );
  }

  export function selectIsAccountsInAllCompanyEmpty(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) =>
      isEmpty(state.accountsByCompany)
    );
  }

  export function selectAccessList(): MemoizedSelector<
    AppState,
    AccessList,
    DefaultProjectorFn<AccessList>
  > {
    return createSelector(
      installationDetails,
      (state: InstallationDetailsState) => state.accessList
    );
  }

  export function selectAccessListUserByCustomer(): MemoizedSelector<
    AppState,
    AccessListUserByCustomerNumber,
    DefaultProjectorFn<AccessListUserByCustomerNumber>
  > {
    return createSelector(installationDetails, (state: InstallationDetailsState) => {
      const { accessList, accountsByCompany } = state;
      const { buildingManager, serviceContractor, operator } = accessList;

      return {
        buildingManager:
          buildingManager?.customerNumber && accountsByCompany[buildingManager?.customerNumber]
            ? {
                name: buildingManager?.companyName,
                value: accountsByCompany[buildingManager?.customerNumber],
              }
            : null,
        serviceContractor:
          serviceContractor?.customerNumber && accountsByCompany[serviceContractor?.customerNumber]
            ? {
                name: serviceContractor?.companyName,
                value: accountsByCompany[serviceContractor?.customerNumber],
              }
            : null,
        operator:
          operator?.customerNumber && accountsByCompany[operator?.customerNumber]
            ? {
                name: operator?.companyName,
                value: accountsByCompany[operator?.customerNumber],
              }
            : null,
      };
    });
  }

  export const selectDynamicAssistantPlan = createSelector(
    installationDetails,
    (state: InstallationDetailsState) => {
      const activeSubscription = state.viCareSubscriptions?.find(
        (subscription) =>
          subscription.plan === DynamicAssistantPlan.Basic ||
          subscription.plan === DynamicAssistantPlan.Pro
      )?.plan;

      return activeSubscription;
    }
  );
}
